/**
 * A frame that can take the whole screen.
 *
 * The browser's own full-screen mode where it is offered — the map is the one
 * thing an operations room wants edge to edge — and a fixed overlay where it is
 * not (an embedded page, a browser that refused), so the button always does
 * something. Escape leaves either way, and the caller renders the same frame
 * with `active` deciding whether it fills the viewport.
 */
import { useCallback, useEffect, useRef, useState } from "react";

export interface Fullscreen<T extends HTMLElement> {
  ref: React.RefObject<T | null>;
  active: boolean;
  enter: () => Promise<void>;
  exit: () => Promise<void>;
  toggle: () => Promise<void>;
}

export function useFullscreen<T extends HTMLElement>(): Fullscreen<T> {
  const ref = useRef<T | null>(null);
  const [native, setNative] = useState(false);
  const [fallback, setFallback] = useState(false);

  useEffect(() => {
    const on = () => setNative(!!document.fullscreenElement && document.fullscreenElement === ref.current);
    document.addEventListener("fullscreenchange", on);
    return () => document.removeEventListener("fullscreenchange", on);
  }, []);

  useEffect(() => {
    if (!fallback) return;
    const key = (e: KeyboardEvent) => {
      if (e.key === "Escape") setFallback(false);
    };
    window.addEventListener("keydown", key);
    return () => window.removeEventListener("keydown", key);
  }, [fallback]);

  const enter = useCallback(async () => {
    const el = ref.current;
    if (!el) return;
    if (typeof el.requestFullscreen === "function") {
      try {
        await el.requestFullscreen();
        return;
      } catch {
        /* refused — the overlay does the job instead */
      }
    }
    setFallback(true);
  }, []);

  const exit = useCallback(async () => {
    if (document.fullscreenElement && typeof document.exitFullscreen === "function") {
      try {
        await document.exitFullscreen();
      } catch {
        /* already gone */
      }
    }
    setFallback(false);
  }, []);

  const active = native || fallback;
  const toggle = useCallback(() => (active ? exit() : enter()), [active, enter, exit]);
  return { ref, active, enter, exit, toggle };
}

/** The style a frame takes while it fills the viewport without the browser's help. */
export const FULLSCREEN_STYLE: React.CSSProperties = { position: "fixed", inset: 0, zIndex: 600, background: "var(--bg)" };
